import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatDate, type AuthUser, type Enrollment, type Certificate } from "@/lib/api";
import WalletModal from "@/components/feature/WalletModal";
import EditStudentProfileModal from "@/components/feature/EditStudentProfileModal";

export default function EstudianteDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [student, setStudent] = useState<AuthUser | null>(null);
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [loading, setLoading] = useState(true);
  const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
  const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);

  const loadData = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [usersData, enrollmentsData, certificatesData] = await Promise.all([
        api.getUsers({ includeDeleted: true }),
        api.getEnrollments(),
        api.getCertificates(),
      ]);
      setStudent(usersData.find((u) => u.id === id) || null);
      setEnrollments(enrollmentsData.filter((e) => e.student_id === id));
      setCertificates(certificatesData.filter((c) => c.student_id === id));
    } catch {
      setStudent(null);
      setEnrollments([]);
      setCertificates([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, [id]);

  const handleDeleteEnrollment = async (enrollmentId: string) => {
    if (!confirm("¿Estás seguro de que deseas retirar a este estudiante del curso? Esta acción eliminará su inscripción y convertirá los pagos en saldo a favor.")) return;
    try {
      await api.deleteEnrollment(enrollmentId);
      toast.success("Estudiante retirado exitosamente. Se ha reembolsado el saldo a favor si aplica.");
      loadData();
    } catch (error: any) {
      toast.error(error.error || "Error al retirar al estudiante");
    }
  };

  const handleAcceptEnrollment = async (enrollmentId: string, status: string) => {
    if (status === "rechazada" && !confirm("Esta inscripción está rechazada. ¿Deseas aceptarla ahora?")) return;
    try {
      await api.acceptEnrollment(enrollmentId);
      toast.success("Inscripción aceptada.");
      loadData();
    } catch (error: any) {
      toast.error(error.message || "Error al aceptar inscripción");
    }
  };

  const handleRejectEnrollment = async (enrollmentId: string) => {
    if (!confirm("¿Rechazar esta inscripción?")) return;
    try {
      await api.rejectEnrollment(enrollmentId);
      toast.success("Inscripción rechazada.");
      loadData();
    } catch (error: any) {
      toast.error(error.message || "Error al rechazar inscripción");
    }
  };

  const handleDeleteStudent = async () => {
    if (!student) return;
    if (!confirm(`¿Seguro que deseas enviar a papelera a ${student.full_name}?\n\nSolo se permite si no tiene cursos inscritos ni saldo a favor.`)) return;
    try {
      await api.deleteUser(student.id);
      toast.success("Estudiante enviado a papelera.");
      navigate("/estudiantes");
    } catch (error: any) {
      toast.error(error.message || "Error al eliminar estudiante");
    }
  };

  const handleRestoreStudent = async () => {
    if (!student) return;
    if (!confirm("¿Seguro que deseas recuperar este estudiante?")) return;
    try {
      await api.restoreUser(student.id);
      toast.success("Estudiante recuperado.");
      loadData();
    } catch (error: any) {
      toast.error(error.message || "Error al recuperar estudiante");
    }
  };

  const handleResendTempPassword = async () => {
    if (!student) return;
    if (!confirm(`¿Reenviar contraseña temporal a ${student.full_name}?\n\nSolo se permite si aún no ha cambiado su contraseña.`)) return;
    try {
      const result = await api.resendTempPassword(student.id);
      if (result.email_sent) {
        toast.success("Contraseña temporal reenviada por correo.");
      } else {
        toast.error("No se pudo enviar el correo. Verifica la configuración SMTP.");
      }
    } catch (error: any) {
      toast.error(error.message || "Error al reenviar contraseña temporal");
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case "pagado":
        return <span className="badge-success">Pagado</span>;
      case "cortesia":
        return <span className="badge-info">Cortesía / Exonerado</span>;
      case "abono_parcial":
        return <span className="badge-warning">Abono Parcial</span>;
      case "pendiente":
        return <span className="badge-danger">Pendiente</span>;
      default:
        return <span className="badge-info">{status}</span>;
    }
  };

  const enrollmentLabel = (status: string) => {
    switch (status) {
      case "aceptada":
        return <span className="badge-success">Aceptada</span>;
      case "rechazada":
        return <span className="badge-danger">Rechazada</span>;
      default:
        return <span className="badge-warning">Pendiente</span>;
    }
  };

  const evalLabel = (status: string) => {
    switch (status) {
      case "aprobado":
        return <span className="badge-success">Aprobado</span>;
      case "rechazado":
        return <span className="badge-danger">Rechazado</span>;
      case "revision":
        return <span className="badge-warning">En Revisión</span>;
      default:
        return <span className="badge-info">Pendiente</span>;
    }
  };

  const attLabel = (status: string) => {
    switch (status) {
      case "presente":
        return <span className="badge-success">Presente</span>;
      case "ausente":
        return <span className="badge-danger">Ausente</span>;
      default:
        return <span className="badge-info">Pendiente</span>;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-20">
        <i className="ri-loader-4-line text-3xl text-[var(--color-text-muted)] animate-spin"></i>
        <p className="text-sm text-[var(--color-text-muted)] mt-3">Cargando estudiante...</p>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="text-center py-20">
        <i className="ri-user-unfollow-line text-4xl text-[var(--color-text-muted)]"></i>
        <p className="text-[var(--color-text-muted)] mt-3">Estudiante no encontrado</p>
        <button onClick={() => navigate("/estudiantes")} className="btn-primary mt-4 cursor-pointer">
          Volver al directorio
        </button>
      </div>
    );
  }

  const getCourseName = (enr: Enrollment) =>
    enr.edition?.course?.name || enr.diplomadoEdition?.diplomado?.name || "Servicio no encontrado";
  const getCourseHours = (enr: Enrollment) =>
    enr.edition?.course?.hours ?? enr.diplomadoEdition?.diplomado?.hours ?? 0;

  return (
    <div className="space-y-6 animate-[fadeIn_0.3s_ease-out]">
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate("/estudiantes")}
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line text-xl"></i>
        </button>
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Perfil del Estudiante</h1>
          <p className="text-sm text-[var(--color-text-muted)] mt-1">Información detallada y trazabilidad completa</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card lg:col-span-1">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-20 h-20 rounded-2xl bg-[var(--color-accent)]/15 flex items-center justify-center mb-4">
              <span className="text-[var(--color-text-accent)] text-2xl font-bold">
                {student.full_name.charAt(0)}
              </span>
            </div>
            <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{student.full_name}</h2>
            <p className="text-sm text-[var(--color-text-accent)]">{student.position || "Sin puesto"}</p>
            <p className="text-xs text-[var(--color-text-secondary)] mt-1">{student.company || "Sin empresa"}</p>
            {!student.deleted_at && (
              <button
                onClick={() => setIsEditProfileOpen(true)}
                className="mt-4 btn-primary text-xs py-2 px-4 flex items-center gap-2 cursor-pointer"
              >
                <i className="ri-edit-line"></i> Corregir nombre / datos
              </button>
            )}
            <p className="text-[10px] text-[var(--color-text-muted)] mt-2">
              El nombre corregido se usa en certificados y validación pública.
            </p>
          </div>

          <div className="space-y-4 border-t border-[var(--color-border)] pt-4">
            <div className="flex items-center gap-3 text-sm">
              <i className="ri-mail-line text-[var(--color-text-muted)]"></i>
              <span className="text-[var(--color-text-secondary)]">{student.email}</span>
            </div>
            <div className="flex items-center gap-3 text-sm">
              <i className="ri-phone-line text-[var(--color-text-muted)]"></i>
              <span className="text-[var(--color-text-secondary)]">{student.phone || "Sin teléfono"}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-6 pt-4 border-t border-[var(--color-border)]">
            <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-[var(--color-text-accent)]">{enrollments.length}</p>
              <p className="text-xs text-[var(--color-text-muted)]">Servicios</p>
            </div>
            <div className="bg-[var(--color-bg-tertiary)] rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-green-400">{certificates.length}</p>
              <p className="text-xs text-[var(--color-text-muted)]">Certificados</p>
            </div>
          </div>

          <div className="mt-6 pt-4 border-t border-[var(--color-border)]">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[var(--color-text-secondary)]">Saldo a favor:</span>
              <span className="text-lg font-bold text-[var(--color-accent)]">
                ${(student.credit_balance || 0).toLocaleString("en-US")} USD
              </span>
            </div>
            <button
              onClick={() => setIsWalletModalOpen(true)}
              disabled={!!student.deleted_at}
              className="btn-secondary w-full text-xs flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <i className="ri-wallet-3-line"></i> Agregar Saldo
            </button>
          </div>

          {!student.deleted_at && (
            <div className="mt-6 pt-4 border-t border-[var(--color-border)]">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-[var(--color-text-secondary)]">Contraseña:</span>
                <span className={`text-xs font-semibold ${student.password_changed_at ? "text-green-400" : "text-yellow-400"}`}>
                  {student.password_changed_at ? "Cambiada" : "Temporal sin cambiar"}
                </span>
              </div>
              {!student.password_changed_at && (
                <button
                  onClick={handleResendTempPassword}
                  className="btn-secondary w-full text-xs flex items-center justify-center gap-2 cursor-pointer"
                >
                  <i className="ri-mail-send-line"></i> Reenviar contraseña temporal
                </button>
              )}
            </div>
          )}

          {student.deleted_at ? (
            <button
              onClick={handleRestoreStudent}
              className="btn-primary w-full mt-4 flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="ri-refresh-line"></i> Recuperar Estudiante
            </button>
          ) : (
            <button
              onClick={handleDeleteStudent}
              className="btn-secondary w-full mt-4 flex items-center justify-center gap-2 text-red-400 border-red-400/30 hover:bg-red-500/10 cursor-pointer"
            >
              <i className="ri-delete-bin-line"></i> Enviar a Papelera
            </button>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <div className="card">
            <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <i className="ri-history-line text-[var(--color-text-accent)]"></i>
              Historial de Servicios Inscritos
            </h3>
            {enrollments.length === 0 ? (
              <p className="text-sm text-[var(--color-text-muted)] text-center py-8">
                Sin servicios registrados
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="table-header">Servicio</th>
                      <th className="table-header">Edición</th>
                      <th className="table-header">Horas</th>
                      <th className="table-header">Asistencia</th>
                      <th className="table-header">Evaluación</th>
                      <th className="table-header">Inscripción</th>
                      <th className="table-header">Pago</th>
                      <th className="table-header">Certificado</th>
                      <th className="table-header"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {enrollments.map((enr) => (
                      <tr key={enr.id} className="hover:bg-[var(--color-bg-card-hover)] transition-colors">
                        <td className="table-cell">
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">
                            {getCourseName(enr)}
                          </span>
                          <p className="text-xs text-[var(--color-text-muted)]">
                            {formatDate(enr.registration_date)}
                          </p>
                        </td>
                        <td className="table-cell">
                          <span className="text-xs text-[var(--color-text-secondary)]">
                            {enr.edition?.edition_label || enr.diplomadoEdition?.edition_label || enr.edition?.location || enr.diplomadoEdition?.location || "—"}
                          </span>
                        </td>
                        <td className="table-cell">
                          <span className="text-sm text-[var(--color-text-secondary)]">{getCourseHours(enr)}h</span>
                        </td>
                        <td className="table-cell">{attLabel(enr.attendance_status)}</td>
                        <td className="table-cell">{evalLabel(enr.evaluation_status)}</td>
                        <td className="table-cell">{enrollmentLabel(enr.enrollment_status)}</td>
                        <td className="table-cell">{statusLabel(enr.payment_status)}</td>
                        <td className="table-cell">
                          {enr.certificate_issued ? (
                            <span className="badge-success">Emitido</span>
                          ) : (
                            <span className="text-xs text-[var(--color-text-muted)]">—</span>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center gap-3 flex-wrap">
                            {enr.payment_status !== "cortesia" && (
                              <button
                                type="button"
                                onClick={async () => {
                                  const alreadyPaid = enr.payment_status === "pagado" || enr.payment_status === "abono_parcial";
                                  if (!confirm(
                                    alreadyPaid
                                      ? `¿Convertir a cortesía/exonerado?\n\nQuedará liquidado y los pagos reales se anularán para que NO sumen a las ganancias.`
                                      : `¿Marcar cortesía/exonerado?\n\nQuedará liquidado para certificados, pero NO sumará a las ganancias.`
                                  )) return;
                                  try {
                                    await api.markEnrollmentCourtesy(enr.id);
                                    loadData();
                                  } catch (err: any) {
                                    alert(err.message || "No se pudo marcar cortesía");
                                  }
                                }}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-[var(--color-accent-oil)]/40 text-[var(--color-accent-oil)] bg-[var(--color-accent-oil)]/10 hover:bg-[var(--color-accent-oil)]/20 transition-colors cursor-pointer"
                                title="Exonerar pago por cortesía (no cuenta como ingreso)"
                              >
                                <i className="ri-gift-2-line text-sm"></i>
                                Cortesía
                              </button>
                            )}
                            {enr.enrollment_status !== "aceptada" && (
                              <button
                                type="button"
                                onClick={() => handleAcceptEnrollment(enr.id, enr.enrollment_status)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-green-500/40 text-green-400 bg-green-500/10 hover:bg-green-500/20 transition-colors cursor-pointer"
                                title="Aceptar inscripción"
                              >
                                <i className="ri-check-line text-sm"></i>
                                Aceptar
                              </button>
                            )}
                            {enr.enrollment_status === "pendiente" && (
                              <button
                                type="button"
                                onClick={() => handleRejectEnrollment(enr.id)}
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-red-500/40 text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors cursor-pointer"
                                title="Rechazar inscripción"
                              >
                                <i className="ri-close-line text-sm"></i>
                                Rechazar
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDeleteEnrollment(enr.id)}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium border border-red-500/40 text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors cursor-pointer"
                              title="Retirar estudiante (reembolsar a saldo a favor)"
                            >
                              <i className="ri-user-unfollow-line text-sm"></i>
                              Retirar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {certificates.length > 0 && (
            <div className="card">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
                <i className="ri-award-line text-[var(--color-text-accent)]"></i>
                Certificados Obtenidos
              </h3>
              <div className="space-y-3">
                {certificates.map((cert) => (
                  <div
                    key={cert.id}
                    className="flex items-center justify-between bg-[var(--color-bg-tertiary)] rounded-xl p-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-green-500/15 flex items-center justify-center flex-shrink-0">
                        <i className="ri-award-line text-green-400 text-lg"></i>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-[var(--color-text-primary)]">
                          {cert.certificate_number}
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)]">
                          {cert.edition?.course?.name || cert.diplomadoEdition?.diplomado?.name || "Servicio"} &middot; {cert.hours_completed}h &middot;{" "}
                          {formatDate(cert.issue_date)}
                        </p>
                      </div>
                    </div>
                    <span className="badge-success text-xs">Activo</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {enrollments.some((e) => e.observations || e.evaluator_notes) && (
            <div className="card">
              <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
                <i className="ri-sticky-note-line text-[var(--color-text-accent)]"></i>
                Observaciones y Notas
              </h3>
              <div className="space-y-3">
                {enrollments
                  .filter((e) => e.observations || e.evaluator_notes)
                  .map((enr) => (
                    <div key={enr.id} className="bg-[var(--color-bg-tertiary)] rounded-xl p-4">
                      <p className="text-xs text-[var(--color-text-muted)] mb-2">
                        {getCourseName(enr)}
                      </p>
                      {enr.observations && (
                        <div className="flex items-start gap-2 mb-2">
                          <span className="text-[10px] text-[var(--color-text-accent)] font-medium uppercase">Instructor:</span>
                          <p className="text-sm text-[var(--color-text-secondary)]">{enr.observations}</p>
                        </div>
                      )}
                      {enr.evaluator_notes && (
                        <div className="flex items-start gap-2">
                          <span className="text-[10px] text-yellow-400 font-medium uppercase">Evaluador:</span>
                          <p className="text-sm text-[var(--color-text-secondary)]">{enr.evaluator_notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      </div>
      <WalletModal
        isOpen={isWalletModalOpen}
        onClose={() => setIsWalletModalOpen(false)}
        studentId={student.id}
        onSuccess={loadData}
      />
      {isEditProfileOpen && (
        <EditStudentProfileModal
          student={student}
          onClose={() => setIsEditProfileOpen(false)}
          onSaved={(updated) => {
            setStudent((prev) => (prev ? { ...prev, ...updated } : prev));
            loadData();
          }}
        />
      )}
    </div>
  );
}
